// `http://`, `https://` or `www.` up to the next whitespace, double quote or
// angle bracket. `\b` keeps a scheme glued to a preceding word (`xhttps://`)
// as text.
const URL_CANDIDATE = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi
const WWW_PREFIX = /^www\./i
const TRAILING_PUNCTUATION = '.,:;!?\''
const OPENING_BRACKET_FOR = { ')': '(', ']': '[', '}': '{' }

const countOf = (value, character) => value.split(character).length - 1

/**
 * Drops sentence punctuation and unbalanced closing brackets from the end of
 * a URL candidate: "(see https://x.fi/survey)." links `https://x.fi/survey`,
 * while `https://en.wikipedia.org/wiki/Elevator_(disambiguation)` keeps its
 * own balanced parentheses.
 *
 * @param {string} candidate
 * @returns {string}
 */
function trimTrailingPunctuation (candidate) {
  let url = candidate
  while (url.length > 0) {
    const last = url[url.length - 1]
    const opening = OPENING_BRACKET_FOR[last]
    const isUnbalancedClosing = opening !== undefined && countOf(url, last) > countOf(url, opening)
    if (!TRAILING_PUNCTUATION.includes(last) && !isUnbalancedClosing) {
      break
    }
    url = url.slice(0, -1)
  }
  return url
}

/**
 * The href for a URL candidate, or null when it is not a valid http(s) URL
 * (e.g. `https://` with no host).
 *
 * @param {string} url
 * @returns {string|null}
 */
function toSafeHref (url) {
  const href = WWW_PREFIX.test(url) ? `https://${url}` : url
  try {
    const { protocol } = new URL(href)
    return protocol === 'http:' || protocol === 'https:' ? href : null
  } catch (error) {
    return null
  }
}

/**
 * Splits plain text into text and link segments so a template can render the
 * URLs in it as anchors without `v-html`.
 *
 * KPT-1162: broadcast ("important") messages are plain strings, and users had
 * to copy-paste a survey URL out of the toast to open it. Rendering segments
 * keeps Vue's escaping on every character of the message — link text is
 * interpolated and the href is an attribute binding — so the markup in a
 * message is shown, never interpreted.
 *
 * Only `http://`, `https://` and `www.` URLs are linked (`www.` gets an
 * `https://` href). Every other scheme (`javascript:`, `data:`, `mailto:`),
 * bare domains and e-mail addresses stay text. Joining the segments' `text`
 * always gives back the original message.
 *
 * @param {string} text
 * @returns {{ text: string, href?: string }[]}
 */
export function splitTextIntoLinkSegments (text) {
  const value = text === null || text === undefined ? '' : String(text)
  const segments = []
  let textStart = 0
  for (const match of value.matchAll(URL_CANDIDATE)) {
    const url = trimTrailingPunctuation(match[0])
    const href = toSafeHref(url)
    if (href === null) {
      continue
    }
    if (match.index > textStart) {
      segments.push({ text: value.slice(textStart, match.index) })
    }
    segments.push({ text: url, href })
    textStart = match.index + url.length
  }
  if (textStart < value.length) {
    segments.push({ text: value.slice(textStart) })
  }
  return segments
}
